import { useCallback, useEffect, useRef } from "react";
import FacebookPixelService from "../shared/services/facebookPixelService";
import { log, logInfo, logError, logWarn } from "../shared/utils/logger.js";

/**
 * React hook for Facebook Pixel event tracking
 * Provides convenient methods for component-level pixel tracking
 */
export const usePixelEventTracker = () => {
  const trackingRef = useRef({
    initialized: false,
    lastTracked: new Map(),
  });

  // Initialize pixel on mount
  useEffect(() => {
    try {
      FacebookPixelService.init();
      trackingRef.current.initialized = true;
    } catch (error) {
      logError("Failed to initialize Facebook Pixel:", error);
    }
  }, []);

  /**
   * Track a generic pixel event
   * @param {string} eventName - Event name
   * @param {Object} parameters - Event parameters
   * @param {Object} options - Tracking options
   * @param {boolean} options.throttle - Throttle duplicate events (default: true)
   * @param {number} options.throttleMs - Throttle duration in ms (default: 5000)
   */
  const trackEvent = useCallback((eventName, parameters = {}, options = {}) => {
    const { throttle = true, throttleMs = 5000 } = options;

    if (!trackingRef.current.initialized) {
      logWarn("Facebook Pixel not initialized, skipping event:", eventName);
      return;
    }

    // Throttle duplicate events
    if (throttle) {
      const eventKey = `${eventName}_${JSON.stringify(parameters)}`;
      const lastTracked = trackingRef.current.lastTracked.get(eventKey);
      const now = Date.now();

      if (lastTracked && now - lastTracked < throttleMs) {
        log(`Throttled duplicate event: ${eventName}`);
        return;
      }

      trackingRef.current.lastTracked.set(eventKey, now);
    }

    try {
      FacebookPixelService.track(eventName, parameters);
    } catch (error) {
      logError(`Failed to track event ${eventName}:`, error);
    }
  }, []);

  /**
   * Track quote view event
   * @param {string} quoteId - Quote ID
   * @param {number} estimatedCost - Quote estimated cost
   */
  const trackQuoteView = useCallback(
    (quoteId, estimatedCost) => {
      trackEvent("ViewContent", {
        content_type: "quote",
        content_ids: [quoteId],
        value: estimatedCost,
        currency: "ZAR",
      });
    },
    [trackEvent]
  );

  /**
   * Track quote acceptance event
   * @param {string} quoteId - Quote ID
   * @param {number} estimatedCost - Quote estimated cost
   */
  const trackQuoteAccept = useCallback(
    (quoteId, estimatedCost) => {
      trackEvent("Purchase", {
        content_type: "quote",
        content_ids: [quoteId],
        value: estimatedCost,
        currency: "ZAR",
      });
    },
    [trackEvent]
  );

  /**
   * Track lead event
   * @param {Object} campaignData - Campaign data
   */
  const trackLead = useCallback(
    (campaignData) => {
      trackEvent("Lead", {
        content_name: "WhatsApp Lead",
        content_category: "logistics_quote",
        value: campaignData.leadValue || 0,
        currency: "ZAR",
        custom_data: campaignData,
      });
    },
    [trackEvent]
  );

  /**
   * Track custom event
   * @param {string} eventName - Custom event name
   * @param {Object} parameters - Event parameters
   */
  const trackCustomEvent = useCallback(
    (eventName, parameters = {}) => {
      trackEvent("CustomEvent", {
        event_name: eventName,
        ...parameters,
      });
    },
    [trackEvent]
  );

  /**
   * Track quote sharing event
   * @param {string} quoteId - Quote ID
   * @param {string} shareMethod - How the quote was shared
   */
  const trackQuoteShare = useCallback(
    (quoteId, shareMethod = "unknown") => {
      trackCustomEvent("QuoteShared", {
        content_type: "quote",
        content_ids: [quoteId],
        share_method: shareMethod,
      });
    },
    [trackCustomEvent]
  );

  /**
   * Track quote rejection event
   * @param {string} quoteId - Quote ID
   * @param {string} reason - Rejection reason
   */
  const trackQuoteReject = useCallback(
    (quoteId, reason = "no_reason") => {
      trackCustomEvent("QuoteRejected", {
        content_type: "quote",
        content_ids: [quoteId],
        rejection_reason: reason,
      });
    },
    [trackCustomEvent]
  );

  /**
   * Check if pixel is initialized
   * @returns {boolean}
   */
  const isInitialized = useCallback(() => {
    return trackingRef.current.initialized;
  }, []);

  return {
    trackEvent,
    trackQuoteView,
    trackQuoteAccept,
    trackLead,
    trackCustomEvent,
    trackQuoteShare,
    trackQuoteReject,
    isInitialized,
  };
};

export default usePixelEventTracker;
